// UI-NAV-1 (2026-05-07) — Operations console.
// OPS-1 (2026-05-29): first real sub-surface — Time Management. Cap-gated
// administrative time entry: enter time on behalf of staff, corrections, and
// migration cleanup. Kept OUT of the adjuster workflow (no peer-surveillance;
// this is a back-office surface for ADMIN_TIME_ENTRY holders only).
// 2026-06-17 (Chunk B): the accounting surfaces (Invoices/AR, Payment Coding,
// Funds Intake, Trust Reconciliation, General Trust) MOVED to the new Accounting
// top-nav peer (pages/Accounting.js). Operations now = intake / routing /
// processing: Bill Intake, Bill Review, Time Management.
import { useState, useEffect } from "react";
import { can, apiFetch } from "../api";
import { Spinner, EmptyState } from "../components/ui";  // UI-SWEEP-OPS
import { useNavigation } from "../components/nav/NavigationContext";

const OP_LABEL = { bills: "Bill Intake", billreview: "Document Review", audit: "Medical Bill Review",
  time: "Time Management", overview: "Overview" };
import { BillIntakeQueue, BillReviewQueue } from "../components/BillIntake";

export default function Operations({ openTime, onWorkChanged, onNavigateToClaim, initialTab, focusBillId }) {
  const canAdminTime = can("admin_time_entry");
  const canBills = can("view_bill_intake");
  const [tab, setTab] = useState(initialTab || (canBills ? "bills" : canAdminTime ? "time" : "overview"));
  // Scope E: a notification deep-link can switch the tab (e.g. to Document Review).
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab, focusBillId]);
  const { setSubLabel } = useNavigation();   // GLOBAL-NAV-3: breadcrumb "Operations › <sub-tab>"
  useEffect(() => { setSubLabel(OP_LABEL[tab] || null); }, [tab, setSubLabel]);

  return (
    <div className="page">
      <div style={{ display: "flex", gap: 8, marginBottom: 16, borderBottom: "1px solid var(--border)" }}>
        {[
          { id: "bills", label: "Bill Intake", show: canBills },
          { id: "billreview", label: "Document Review", show: canBills },
          { id: "audit", label: "Medical Bill Review", show: canBills },
          { id: "time", label: "Time Management", show: canAdminTime },
          { id: "overview", label: "Overview", show: true },
        ].filter(t => t.show).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  style={{ padding: "8px 14px", border: "none", borderBottom: tab === t.id ? "2px solid var(--navy)" : "2px solid transparent",
                           background: "transparent", cursor: "pointer", fontSize: 13, fontWeight: 600,
                           color: tab === t.id ? "var(--navy)" : "var(--text2)" }}>{t.label}</button>
        ))}
      </div>

      {tab === "bills" && canBills && <BillIntakeQueue />}
      {tab === "billreview" && canBills && <BillReviewQueue onNavigateToClaim={onNavigateToClaim} focusBillId={focusBillId} />}
      {tab === "audit" && canBills && <AuditQueue onNavigateToClaim={onNavigateToClaim} />}

      {tab === "time" && canAdminTime && (
        <div className="card">
          <div className="card-header"><h3>Time Management</h3></div>
          <div className="card-body">
            <p style={{ color: "var(--text2)", fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
              Enter time on behalf of staff (migration cleanup, corrections, or logging for an adjuster).
              The acting admin is recorded in the audit trail; the entry is attributed to the selected staff member.
            </p>
            <button className="btn btn-blue" onClick={() => openTime && openTime({ adminMode: true })}>
              + Log Time for Staff
            </button>
          </div>
        </div>
      )}

      {tab === "overview" && (
        <div className="card">
          <div className="card-header"><h3>Operations</h3></div>
          <div className="card-body">
            <p style={{ color: "var(--text2)", fontSize: 13, lineHeight: 1.6 }}>
              The Operations console hosts intake &amp; routing: <strong>Bill Intake</strong>,
              <strong> Document Review</strong>, <strong>Medical Bill Review</strong>, and
              <strong> Time Management</strong>.
              Approvals, AR, payment coding, funds intake, trust, and payroll moved to
              the <strong>Accounting</strong> console.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// M2 (Kuhrt 2026-06-30): firm-wide Medical Bill Review queue — every bill out for audit, with the
// billed → approved → savings split and the linked fee bill. Row → open the claim (the audit detail
// lives inline on the claim's bill). TPA-walled server-side.
function AuditQueue({ onNavigateToClaim }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiFetch("/audit-queue").then(d => { setRows(d.audits || []); setLoading(false); })
      .catch(e => { setErr(String(e.message || e)); setLoading(false); });
  }, []);
  const money = (v) => (v == null ? "—" : "$" + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }));
  const ST = {
    sent: { t: "At audit", c: "#b45309", bg: "#fffbeb" },
    returned: { t: "EOB returned", c: "#166534", bg: "#dcfce7" },
    reconsideration: { t: "Reconsideration", c: "#92400e", bg: "#fef3c7" },
  };
  const th = { padding: "8px 10px", textAlign: "left", fontSize: 11, color: "var(--text3)", textTransform: "uppercase" };
  const td = { padding: "8px 10px", fontSize: 13 };
  return (
    <div className="card">
      <div className="section-header"><span className="section-title">Medical Bill Review — at audit ({rows.length})</span></div>
      {err && <div className="alert-error" style={{ margin: 12 }}>{err}</div>}
      {loading ? <Spinner label="Loading audits…" /> : rows.length === 0 ? (
        <EmptyState icon="⚑" title="No bills at audit" hint="Bills sent to medical bill review will appear here." />
      ) : (
        <table style={{ width: "100%" }}>
          <thead><tr>
            <th style={th}>Claim</th><th style={th}>Vendor</th><th style={th}>Bill</th><th style={th}>Status</th>
            {/* WS0-B H22: original_amount is the RESPONSIBLE amount sent to audit, not the
                full billed total (which lives in original_total_bill_amount). */}
            <th style={{ ...th, textAlign: "right" }}>Sent to audit</th><th style={{ ...th, textAlign: "right" }}>Approved</th>
            <th style={{ ...th, textAlign: "right" }}>Savings</th><th style={th}>Fee bill</th>
          </tr></thead>
          <tbody>
            {rows.map(r => {
              const s = ST[r.audit_status] || { t: r.audit_status, c: "var(--text2)", bg: "#f1f5f9" };
              return (
                <tr key={r.audit_id} style={{ borderTop: "1px solid var(--border)", cursor: "pointer" }}
                    onClick={() => onNavigateToClaim && r.claim_id && onNavigateToClaim(r.claim_id)}>
                  <td style={td}><span className="mono" style={{ color: "var(--blue)" }}>{r.claim_number || "—"}</span></td>
                  <td style={td}>{r.vendor_name || "—"}</td>
                  <td style={td}>{r.payee || "—"}</td>
                  <td style={td}><span style={{ fontSize: 11, fontWeight: 600, color: s.c, background: s.bg, borderRadius: 4, padding: "1px 7px" }}>{s.t}</span></td>
                  <td style={{ ...td, textAlign: "right" }} className="mono">{money(r.original_amount)}</td>
                  <td style={{ ...td, textAlign: "right" }} className="mono">{money(r.final_amount)}</td>
                  <td style={{ ...td, textAlign: "right", color: r.savings > 0 ? "#166534" : "var(--text3)" }} className="mono">{r.savings != null ? money(r.savings) : "—"}</td>
                  <td style={td}>{r.fee_bill_id ? <span style={{ fontSize: 11, color: "var(--text2)" }}>#{r.fee_bill_id} · {r.fee_status}</span> : <span style={{ fontSize: 11, color: "var(--text3)" }}>—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
